import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pencil, Linkedin, Globe, ExternalLink, NotebookTabs, Loader2, AlertCircle, Plus } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface ProfessionalDetailsCardProps {
  linkedinUrl: string;
  websiteUrl: string;
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onLinkedinUrlChange: (value: string) => void;
  onWebsiteUrlChange: (value: string) => void;
}

interface UrlErrors {
  linkedin?: string;
  website?: string;
}

/**
 * Normalizes a URL by prepending https:// if it starts with www.
 */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Validates a URL, allowing both https:// and www. prefixes
 * For LinkedIn URLs, also validates that it's actually a LinkedIn URL
 */
function validateUrl(url: string, isLinkedIn: boolean = false): string | undefined {
  const trimmed = url.trim();
  
  // Empty URLs are allowed (optional fields)
  if (!trimmed) {
    return undefined;
  }

  // Check if URL starts with valid prefix
  const startsWithHttp = trimmed.startsWith('http://') || trimmed.startsWith('https://');
  const startsWithWww = trimmed.startsWith('www.');
  
  if (!startsWithHttp && !startsWithWww) {
    return isLinkedIn 
      ? 'LinkedIn URL must start with https:// or www.'
      : 'Website URL must start with https:// or www.';
  }

  // Normalize URL for validation: if it starts with www., prepend https://
  let urlToValidate = trimmed;
  if (startsWithWww) {
    // Validate www. is followed by a domain with proper structure
    if (trimmed.length <= 4) {
      return isLinkedIn
        ? 'Please enter a valid LinkedIn URL'
        : 'Please enter a valid website URL';
    }
    
    // For www. URLs, check that there's content after www. and at least one dot
    // e.g., "www.domain.com" has dots, but "www.domain" doesn't have enough
    const afterWww = trimmed.substring(4); // Remove "www."
    const dotCount = (afterWww.match(/\./g) || []).length;
    
    // Must have at least one dot after www. for domain.tld format
    if (dotCount === 0 || afterWww.length < 3) {
      return isLinkedIn
        ? 'Please enter a valid LinkedIn URL'
        : 'Please enter a valid website URL with a domain extension (e.g., .com, .org)';
    }
    
    urlToValidate = `https://${trimmed}`;
  }

  // Validate URL format using URL constructor
  let urlObj: URL;
  try {
    urlObj = new URL(urlToValidate);
  } catch {
    return isLinkedIn
      ? 'Please enter a valid LinkedIn URL'
      : 'Please enter a valid website URL';
  }

  // Validate that the URL has a proper hostname
  if (!urlObj.hostname || urlObj.hostname.length < 3) {
    return isLinkedIn
      ? 'Please enter a valid LinkedIn URL'
      : 'Please enter a valid website URL';
  }

  // For LinkedIn URLs, verify it's actually a LinkedIn URL
  if (isLinkedIn) {
    const hostname = urlObj.hostname.toLowerCase();
    // Remove www. prefix if present for comparison
    const normalizedHostname = hostname.replace(/^www\./, '');
    
    // Check if it's a LinkedIn domain
    const isLinkedInDomain = normalizedHostname === 'linkedin.com' || 
                            normalizedHostname.endsWith('.linkedin.com');
    
    if (!isLinkedInDomain) {
      return 'Please enter a valid LinkedIn URL (must be from linkedin.com)';
    }

    // Additional check: LinkedIn profile URLs should typically have /in/ or /pub/
    const pathname = urlObj.pathname.toLowerCase();
    if (!pathname || (pathname === '/' && !urlObj.search)) {
      return 'Please enter a complete LinkedIn profile URL';
    }
  }

  // For website URLs, ensure it has a proper domain with TLD
  if (!isLinkedIn) {
    // Validate it's not just "https://" or "https://www."
    if (urlObj.hostname.length <= 4) {
      return 'Please enter a valid website URL with a domain name';
    }

    // For localhost, allow it
    if (urlObj.hostname === 'localhost') {
      return undefined;
    }

    // Remove www. prefix if present to check domain structure
    let domainToCheck = urlObj.hostname.toLowerCase();
    if (domainToCheck.startsWith('www.')) {
      domainToCheck = domainToCheck.substring(4); // Remove "www."
    }

    // After removing www., we need at least domain.tld format
    // Count the dots - valid domains should have at least one dot (domain.tld)
    const dotCount = (domainToCheck.match(/\./g) || []).length;
    
    // Must have at least one dot for domain.tld
    if (dotCount === 0) {
      return 'Please enter a valid website URL with a domain name and extension (e.g., .com, .org)';
    }

    // Split by dots to validate parts
    const parts = domainToCheck.split('.');
    
    // Ensure we have at least domain and TLD
    if (parts.length < 2) {
      return 'Please enter a valid website URL with a domain name and extension (e.g., .com, .org)';
    }

    // Ensure TLD (last part) is at least 2 characters (e.g., .com, .io)
    const tld = parts[parts.length - 1];
    if (tld.length < 2) {
      return 'Please enter a valid website URL with a proper domain extension (e.g., .com, .org)';
    }

    // Ensure domain name (first part) is not empty
    if (parts[0].length === 0) {
      return 'Please enter a valid website URL with a domain name';
    }
  }

  return undefined; // Validation passed
}

export function ProfessionalDetailsCard({
  linkedinUrl,
  websiteUrl,
  isEditing,
  isSaving,
  onEditClick,
  onCancel,
  onSave,
  onLinkedinUrlChange,
  onWebsiteUrlChange,
}: ProfessionalDetailsCardProps) {
  const [errors, setErrors] = useState<UrlErrors>({});
  const previousLinkedinUrlRef = useRef<string>('');
  const previousWebsiteUrlRef = useRef<string>('');
  const prevIsEditingRef = useRef<boolean>(false);

  // Track previous values when editing starts and validate on enter
  useEffect(() => {
    // Only capture the previous values when transitioning from non-editing to editing
    if (isEditing && !prevIsEditingRef.current) {
      previousLinkedinUrlRef.current = linkedinUrl;
      previousWebsiteUrlRef.current = websiteUrl;
      
      // Only validate URL formats when entering edit mode, not empty fields
      const linkedinUrlError = validateUrl(linkedinUrl, true);
      const websiteUrlError = validateUrl(websiteUrl, false);
      
      const initialErrors: UrlErrors = {};
      if (linkedinUrlError) {
        initialErrors.linkedin = linkedinUrlError;
      }
      if (websiteUrlError) {
        initialErrors.website = websiteUrlError;
      }
      setErrors(initialErrors);
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, linkedinUrl, websiteUrl]);

  const validateEmptyFields = (linkedin: string, website: string): { linkedin?: string; website?: string } => {
    const trimmedLinkedin = linkedin.trim();
    const trimmedWebsite = website.trim();
    
    // Check if both fields are empty
    if (!trimmedLinkedin && !trimmedWebsite) {
      return {
        linkedin: 'Please provide at least one social link',
        website: 'Please provide at least one social link'
      };
    }
    
    return {};
  };

  const handleLinkedinUrlChange = (value: string) => {
    onLinkedinUrlChange(value);
    // Only validate URL format, not empty fields (empty is allowed for individual fields)
    const urlError = validateUrl(value, true);
    const websiteUrlError = validateUrl(websiteUrl, false);
    
    setErrors({
      linkedin: urlError,
      website: websiteUrlError
    });
  };

  const handleWebsiteUrlChange = (value: string) => {
    onWebsiteUrlChange(value);
    // Only validate URL format, not empty fields (empty is allowed for individual fields)
    const urlError = validateUrl(value, false);
    const linkedinUrlError = validateUrl(linkedinUrl, true);
    
    setErrors({
      linkedin: linkedinUrlError,
      website: urlError
    });
  };

  const handleLinkedinBlur = () => {
    // Only validate URL format, not empty fields (empty is allowed for individual fields)
    const urlError = validateUrl(linkedinUrl, true);
    setErrors(prev => ({
      ...prev,
      linkedin: urlError
    }));
  };

  const handleWebsiteBlur = () => {
    // Only validate URL format, not empty fields (empty is allowed for individual fields)
    const urlError = validateUrl(websiteUrl, false);
    setErrors(prev => ({
      ...prev,
      website: urlError
    }));
  };

  const handleCancel = () => {
    // Restore previous values
    onLinkedinUrlChange(previousLinkedinUrlRef.current);
    onWebsiteUrlChange(previousWebsiteUrlRef.current);
    setErrors({});
    onCancel();
  };

  const handleSave = () => {
    // Validate URL formats first
    const linkedinUrlError = validateUrl(linkedinUrl, true);
    const websiteUrlError = validateUrl(websiteUrl, false);
    
    const newErrors: UrlErrors = {};
    if (linkedinUrlError) {
      newErrors.linkedin = linkedinUrlError;
    }
    if (websiteUrlError) {
      newErrors.website = websiteUrlError;
    }
    
    // Check if at least one valid URL is provided
    const linkedinIsValid = !linkedinUrlError && linkedinUrl.trim();
    const websiteIsValid = !websiteUrlError && websiteUrl.trim();
    const hasAtLeastOneValidUrl = linkedinIsValid || websiteIsValid;
    
    // If both are empty or both have errors, show error on both fields
    if (!hasAtLeastOneValidUrl) {
      const emptyErrors = validateEmptyFields(linkedinUrl, websiteUrl);
      if (emptyErrors.linkedin) {
        newErrors.linkedin = emptyErrors.linkedin;
      }
      if (emptyErrors.website) {
        newErrors.website = emptyErrors.website;
      }
    }
    
    setErrors(newErrors);
    
    // Only proceed with save if there are no errors and at least one valid URL is provided
    const hasErrors = Object.keys(newErrors).length > 0;
    if (!hasErrors && hasAtLeastOneValidUrl) {
      onSave();
    }
  };

  const hasErrors = Object.values(errors).some(error => error !== undefined);
  // Check if at least one valid URL is provided (valid = no URL error and not empty)
  const linkedinIsValid = !errors.linkedin && linkedinUrl.trim();
  const websiteIsValid = !errors.website && websiteUrl.trim();
  const hasAtLeastOneValidUrl = linkedinIsValid || websiteIsValid;
  const isEmpty = !linkedinUrl && !websiteUrl && !isEditing;

  return (
    <div className={cn(
      "rounded-xl sm:rounded-[20px] p-4 sm:p-6 shadow-sm",
      isEmpty
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <NotebookTabs className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-900">Social Links</h3>
          {isEmpty && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Recommended
            </Badge>
          )}
        </div>
        {!isEditing && !isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditClick}
            className="text-[#4669ed]"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="linkedin" className="text-sm">LinkedIn</Label>
            <Input
              id="linkedin"
              placeholder="https://linkedin.com/in/... or www.linkedin.com/in/..."
              value={linkedinUrl}
              onChange={(e) => handleLinkedinUrlChange(e.target.value)}
              onBlur={handleLinkedinBlur}
              className={errors.linkedin ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {errors.linkedin && (
              <p className="text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.linkedin}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="website" className="text-sm">Personal Website</Label>
            <Input
              id="website"
              placeholder="https://yoursite.com or www.yoursite.com"
              value={websiteUrl}
              onChange={(e) => handleWebsiteUrlChange(e.target.value)}
              onBlur={handleWebsiteBlur}
              className={errors.website ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {errors.website && (
              <p className="text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.website}
              </p>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCancel} 
              disabled={isSaving}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave} 
              disabled={isSaving || hasErrors || !hasAtLeastOneValidUrl}
              className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {linkedinUrl && (
            <a href={normalizeUrl(linkedinUrl)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline">
              <Linkedin className="h-4 w-4" />
              LinkedIn
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {websiteUrl && (
            <a href={normalizeUrl(websiteUrl)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline">
              <Globe className="h-4 w-4" />
              Website
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {!linkedinUrl && !websiteUrl && (
            <div className="flex flex-col items-center py-4">
              {/* Icon Circle */}
              <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-6">
                <NotebookTabs className="w-6 h-6 text-slate-500" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-normal text-slate-900 text-center mb-2">
                Add Social Links
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 text-center max-w-[280px] leading-relaxed mb-6">
                Connect your LinkedIn profile and website to showcase your work.
              </p>

              {/* CTA Button */}
              <Button
                onClick={onEditClick}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
              >
                <Plus className="w-5 h-5" />
                Add Social Links
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
